import { capitalizeEachWordFirstLetter } from '@/util-conversion/misc/capitalize-each-word-first-letter';
import { cn } from '@/shadcn-ui/utils';
import {
  RadioGroup,
  RadioGroupItem,
  RadioGroupProps,
  RadioItemLabel,
} from '@/shadcn-ui/radio-group';
import { VehiclePlan } from '@/catalog/types/vehicle';
import Spacer from '@/common/components/spacer';
import useMileagePackageCopy from '@/catalog/hooks/use-mileage-package-copy';
import { PRICING } from '@/common/constants/pricing';
import Rule from '@/common/components/rule';
import Check from '@/icons/check';
import PDPPlanHighlightPill from './pdp-plan-highlight-tag';

export interface MileagePackageRadioGroupProps extends RadioGroupProps {
  vehiclePlans: VehiclePlan[];
  selectedPlanId: string;
  /**
   * The current plan the user is on.
   */
  currentPlanId?: string;
  mostPopularPlanMileageAmount?: string;
  splitPayment?: boolean;
  error?: string;
  disabled?: boolean;
  variant?: 'no-radio';
  mostPopularPlanMileagePercentage?: string;
}

export default function MileagePackageRadioGroup({
  vehiclePlans,
  selectedPlanId,
  currentPlanId,
  mostPopularPlanMileageAmount,
  splitPayment = false,
  error,
  disabled: _disabled,
  variant,
  onValueChange: _onValueChange,
  ...props
}: MileagePackageRadioGroupProps) {
  const plans = useMileagePackageCopy({
    plans: vehiclePlans,
    mostPopularPlanMileageAmount,
    splitPayment,
  });

  const zeroMilePlanId = plans?.find((plan) => plan.isZeroMilePlan)?.id;
  const onValueChange = (value: string) => {
    if (value === String(zeroMilePlanId) && splitPayment) {
      return;
    }

    _onValueChange?.(value);
  };

  return (
    <>
      <RadioGroup
        name="plan"
        defaultValue={selectedPlanId}
        className="gap-[8px]"
        onValueChange={onValueChange}
        {...props}
      >
        {plans
          ? plans.map((plan) => {
              const disabled = plan.isZeroMilePlan && splitPayment ? true : _disabled;

              return (
                <>
                  <RadioItemLabel
                    key={plan.planName}
                    htmlFor={plan.planName}
                    selected={selectedPlanId === String(plan.id)}
                    disabled={plan.isZeroMilePlan && splitPayment ? true : disabled}
                    className={cn('p-0 transition-all', {
                      'shadow-shadow30 ring-flexcar-blue40':
                        selectedPlanId === String(plan.id),
                      'shadow-shadow2': selectedPlanId !== String(plan.id),
                      'bg-[linear-gradient(180deg,_#FFFFFF,_#FBFBFB)]':
                        plan.planName !== PRICING.PLAN_NAMES.ZERO_MILE,
                    })}
                  >
                    <div className="flex flex-col items-end pt-[16px]">
                      <div
                        className={cn(
                          'flex w-full flex-col gap-[8px] rounded-2xl px-[16px]',
                          {
                            'pb-0': plan.isMostPopular,
                            'rounded-b-2xl pb-[16px]': !plan.isMostPopular,
                          }
                        )}
                      >
                        {currentPlanId === String(plan.id) ? (
                          <div className="w-fit rounded-full bg-flexcar-neutral90 px-2 py-1">
                            <p className="body3-strong text-flexcar-neutral50">
                              Current plan
                            </p>
                          </div>
                        ) : null}

                        <div className="flex flex-row justify-between">
                          <div className="flex flex-row items-center justify-center gap-3">
                            <RadioGroupItem
                              value={String(plan.id)}
                              id={plan.planName}
                              className={cn({
                                'hidden': variant === 'no-radio' || disabled,
                                'ring-2': variant !== 'no-radio',
                              })}
                              selected={selectedPlanId === String(plan.id)}
                            />
                            {plan.commuterCopy ? (
                              <p
                                className={cn('body-strong', {
                                  'text-flexcar-neutral70': disabled,
                                })}
                              >
                                {capitalizeEachWordFirstLetter(plan.commuterCopy)}
                              </p>
                            ) : null}
                          </div>

                          <p
                            className={cn('body', {
                              'text-flexcar-neutral70': disabled,
                            })}
                          >
                            {plan.monthlyCostCopy}
                          </p>
                        </div>

                        <div className="flex flex-col gap-[4px]">
                          <div className="flex items-center gap-[4px]">
                            <Check />
                            <p
                              className={cn('body2 text-flexcar-neutral10', {
                                'text-flexcar-neutral10': !disabled,
                                'text-flexcar-neutral70': disabled,
                              })}
                            >
                              {plan.mileageIncludedCopy}
                            </p>
                          </div>
                          {plan.isMostPopular ? (
                            <>
                              {variant !== 'no-radio' ? (
                                <Spacer variant="height8" />
                              ) : null}
                              <div className="flex items-center gap-[4px]">
                                <Check />
                                <p className="body2">30%+ savings on additional miles</p>
                              </div>
                            </>
                          ) : null}

                          {variant !== 'no-radio' ? <Spacer variant="height8" /> : null}
                        </div>
                      </div>
                      {plan.isMostPopular ? (
                        <PDPPlanHighlightPill text="Best Value" disabled={disabled} />
                      ) : null}
                    </div>
                  </RadioItemLabel>

                  {plan.planName === PRICING.PLAN_NAMES.EXTRA_HIGH_MILE ? (
                    <Rule direction="horizontal" />
                  ) : null}
                </>
              );
            })
          : null}
      </RadioGroup>

      {error ? (
        <p id="plan-error-text" className="body3 text-flexcar-red40">
          {error}
        </p>
      ) : null}
    </>
  );
}
